import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "./vfs_fonts";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

let pdfReadyPromise = null;

function setupFonts() {
  if (!pdfMake.vfs || Object.keys(pdfMake.vfs).length === 0) {
    const vfs = pdfFonts?.pdfMake?.vfs || pdfFonts?.default?.pdfMake?.vfs;
    if (vfs) pdfMake.vfs = vfs;
  }

  pdfMake.fonts = {
    THSarabunNew: {
      normal: "THSarabunNew.ttf",
      bold: "THSarabunNew_Bold.ttf",
      italics: "THSarabunNew_Italic.ttf",
      bolditalics: "THSarabunNew_BoldItalic.ttf",
    },
  };
}

async function ensurePdfMakeReady() {
  if (!pdfReadyPromise) {
    pdfReadyPromise = (async () => {
      setupFonts();

      await Promise.resolve();

      if (!pdfMake.vfs || Object.keys(pdfMake.vfs).length === 0) {
        throw new Error("pdfMake vfs not loaded");
      }
      return true;
    })();
  }
  return pdfReadyPromise;
}
function formatThaiDateTime(isoString) {
  const date = new Date(isoString);
  const days = [
    "อาทิตย์",
    "จันทร์",
    "อังคาร",
    "พุธ",
    "พฤหัสบดี",
    "ศุกร์",
    "เสาร์",
  ];
  const dayName = days[date.getDay()];
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear() + 543;
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${dayName} ${day}/${month}/${year} ${hours}:${minutes}`;
}
async function getBase64ImageFromUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
function estimateDetailLines(text = "") {
  const avgCharsPerLine = 60; // สำหรับ A4 + fontSize 10
  return Math.ceil(text.length / avgCharsPerLine);
}

export async function exportPurchaseOrder(data) {
  await ensurePdfMakeReady();

  const items = data.items.map((i) => `${i.product.title}|${i.purchasePrice}`);
  const logoBase64 = data.branch.image
    ? await getBase64ImageFromUrl(data.branch.image)
    : null;

  const PAGE_HEIGHT = 920; // A4
  const PAGE_MARGIN_TOP = 20;
  const PAGE_MARGIN_BOTTOM = 20;

  const TOP_CONTENT_HEIGHT = 230; // ส่วนหัว + ลูกค้า
  const SIGNATURE_HEIGHT = 120; // พื้นที่ลายเซ็นทั้งหมด
  const DETAIL_LINE_HEIGHT = 14;
  const ROW_HEIGHT = 20;
  const detailLines = estimateDetailLines(data.detail2);
  const detailHeight = detailLines * DETAIL_LINE_HEIGHT;
  const AVAILABLE_TABLE_HEIGHT =
    PAGE_HEIGHT -
    PAGE_MARGIN_TOP -
    PAGE_MARGIN_BOTTOM -
    TOP_CONTENT_HEIGHT -
    SIGNATURE_HEIGHT -
    detailHeight;

  const maxRowsPerColumn = Math.max(
    3, // กันตารางสั้นเกิน
    Math.floor(AVAILABLE_TABLE_HEIGHT / ROW_HEIGHT)
  );
  const colsPerPage = 4;
  const itemsPerPage = maxRowsPerColumn * colsPerPage;

  const pages = [];

  for (let start = 0; start < items.length; start += itemsPerPage) {
    const pageItems = items.slice(start, start + itemsPerPage);

    const columns = [];
    for (let c = 0; c < colsPerPage; c++) {
      const colItems = pageItems.slice(
        c * maxRowsPerColumn,
        (c + 1) * maxRowsPerColumn
      );
      while (colItems.length < maxRowsPerColumn) colItems.push("|");
      columns.push(colItems);
    }

    const rows = [];
    for (let r = 0; r < maxRowsPerColumn; r++) {
      const row = [];
      for (let c = 0; c < colsPerPage; c++) {
        const [name, price] = (columns[c][r] || "|").split("|");
        row.push({
          text: name || " ",
          alignment: "left",
          margin: [2, 0.2, 0, 0.2],
        });
        row.push({
          text: price ? Number(price).toFixed(2) : " ",
          alignment: "center",
          margin: [0, 0.2, 2, 0.2],
        });
      }
      rows.push(row);
    }

    pages.push(rows);
  }

  const tableHeader = [
    { text: "รายการสินค้า", style: "tableHeader", alignment: "center" },
    { text: "ราคา/หน่วย", style: "tableHeader", alignment: "center" },
    { text: "รายการสินค้า", style: "tableHeader", alignment: "center" },
    { text: "ราคา/หน่วย", style: "tableHeader", alignment: "center" },
    { text: "รายการสินค้า", style: "tableHeader", alignment: "center" },
    { text: "ราคา/หน่วย", style: "tableHeader", alignment: "center" },
    { text: "รายการสินค้า", style: "tableHeader", alignment: "center" },
    { text: "ราคา/หน่วย", style: "tableHeader", alignment: "center" },
  ];

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [30, 20, 30, 20],
    content: [
      {
        columns: [
          {
            text: data.branch.companyName,
            style: "header",
            alignment: "center",
          },
        ],
      },
      {
        text: "Purchase Order",
        alignment: "center",
        margin: [0, 4, 0, 10],
        bold: true,
      },
      {
        columns: [
          {
            width: 65,
            image: logoBase64 || "",
            fit: [60, 60],
          },
          {
            width: "60%",
            stack: [
              { text: `นามลูกค้า: ${data.customer.name}`, fontSize: 11 },
              { text: `โทร: ${data.customer.phone}`, fontSize: 11 },
              { text: `ที่อยู่: ${data.customer.address}`, fontSize: 11 },
              {
                text: data.car
                  ? `รถขนส่ง: ${data.car.type} ${data.car.license}`
                  : "รถขนส่ง: -",
                fontSize: 11,
              },
            ],
          },
          {
            width: "40%",
            stack: [
              {
                text: `Contract Date: ${formatThaiDateTime(data.createdAt)}`,
                fontSize: 11,
              },
              {
                text: `PO #${data?.poNumber && data?.poNumber.split("-").pop()}`,
                fontSize: 11,
              },
              {
                text: `ผู้ออกเอกสาร: ${data.createdBy.username}`,
                fontSize: 11,
              },
            ],
            margin: [0, 0, 0, 0],
          },
        ],
      },
      ...pages.map((rows, idx) => ({
        table: {
          headerRows: 1,
          widths: [90, 26, 90, 26, 90, 26, 90, 26],
          body: [tableHeader, ...rows],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => "#ccc",
          vLineColor: () => "#ccc",
        },
        fontSize: 10,
        pageBreak: idx === 0 ? undefined : "before",
        margin: [0, 5, 0, 5],
      })),
      { text: data.detail2, fontSize: 10 },
      { text: "\n" },
      {
        columns: [
          {
            text: "ลงชื่อ: _________________________(ผู้ตกลงขายสินค้า)",
            alignment: "center",
          },
          {
            text: "ลงชื่อ: _________________________(ผู้ออกเอกสาร)",
            alignment: "center",
          },
          {
            text: "ลงชื่อ: _________________________(การเงิน)",
            alignment: "center",
          },
        ],
        fontSize: 10,
      },
      { text: "\n" },
      {
        columns: [
          { width: "*", text: "" },
          {
            width: "auto",
            text: "ลงชื่อ: _________________________(ผู้อนุมัติ)",
            alignment: "center",
          },
          {
            width: "auto",
            text: "ลงชื่อ: _________________________(ผู้ตรวจสอบเอกสาร)",
            alignment: "center",
            margin: [58, 0, -7, 0],
          },
        ],
        fontSize: 10,
      },
    ],
    styles: {
      header: { fontSize: 16, bold: true },
      tableHeader: { bold: true, fillColor: "#f0f0f0" },
    },
    defaultStyle: { font: "THSarabunNew" },
  };

  pdfMake.createPdf(docDefinition).open();
}
